// src/components/dashboard/pages/WeighbridgeTicketList/index.jsx
import React from 'react';
import DataTable from 'react-data-table-component';
import { Truck, Eye, Pen, User, MapPin, Download, Calendar, FileSpreadsheet, InfoIcon } from 'lucide-react';
import i18n from '../../../../i18n';
import { SideBar } from '../../sidebar';
import NoData from '../../../common/NoData';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import StatusTabs from '../../../common/StatusTab';
import HeaderBanner from '../../../common/HeaderBanner';
import { getWeighbridgeTicketList, exportWeighbridgeTickets } from './services/WeighbridgeTicketService';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import { Link } from 'react-router-dom';
import FilterSection from '../../../common/Filter';
import { toast } from 'react-toastify';

export class WeighbridgeTicketList extends React.Component {
    constructor() {
        super();
        const today = new Date().toISOString().split('T')[0];

        this.state = {
            search: {
                vehicle_id: '',
                delivery_site_id: '',
                status: '',
                driver_id: '',
                ticket_number: '',
                date: today,
            },
            status: 'all',
            tickets: [],
            selectedTicket: {},
            statusData: [
                { key: 'all', label: 'All', count: 0 },
                { key: 'IN', label: 'In Progress', count: 0 },
                { key: 'OUT', label: 'Completed', count: 0 },
            ],
            is_loading: false,
            is_exporting: false,
            limit: 15,
            page: 1,
            total: 0,
            hideSidebar: false,
            showModal: false,
            // Export modal state
            showExportModal: false,
            exportFilters: {
                start_date: today,
                end_date: today,
                delivery_site_id: '',
                status: 'OUT',
                export_excel: true
            }
        };

        // Bind methods
        this.handlePageChange = this.handlePageChange.bind(this);
        this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
        this.handleExport = this.handleExport.bind(this);
        this.handleExportFilterChange = this.handleExportFilterChange.bind(this);
        this.exportToExcel = this.exportToExcel.bind(this);
    }

    componentDidMount() {
        window.scrollTo(0, 0);

        // Get today's date in YYYY-MM-DD format
        const today = new Date().toISOString().split('T')[0];

        // Set start date to a month ago to ensure we include all data
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        const oneMonthAgoStr = oneMonthAgo.toISOString().split('T')[0];

        const params = new URLSearchParams(window.location.search);
        const search = {
            vehicle_id: params.get('vehicle_id') || '',
            delivery_site_id: params.get('delivery_site_id') || '',
            status: params.get('status') || '',
            driver_id: params.get('driver_id') || '',
            ticket_number: params.get('ticket_number') || '',
            date: params.get('date') || today,
        };

        const status = params.get('status') || 'all';
        const page = parseInt(params.get('page')) || 1;
        const limit = parseInt(params.get('limit')) || 15;

        // Initialize export filters with wider date range
        this.setState({
            search,
            status,
            page,
            limit,
            exportFilters: {
                ...this.state.exportFilters,
                start_date: oneMonthAgoStr,  // Use one month ago
                end_date: today
            }
        }, () => {
            this.fetchWeighbridgeTickets();
        });
    }

    updateUrlQuery(q) {
        const queryString = new URLSearchParams(
            Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null))
        ).toString();
        window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
    }

    async fetchWeighbridgeTickets() {
        this.setState({ is_loading: true });
        let { limit, page, status, search } = this.state;

        try {
            // Build filters object
            const filters = {
                page: page,
                per_page: limit,
            };

            // Add status filter if not 'all'
            if (status && status !== 'all') {
                filters.status = status;
            }

            // Add search filters
            if (search.vehicle_id) {
                filters.vehicle_id = search.vehicle_id;
            }

            if (search.delivery_site_id) {
                filters.delivery_site_id = search.delivery_site_id;
            }

            if (search.driver_id) {
                filters.driver_id = search.driver_id;
            }

            if (search.ticket_number) {
                filters.ticket_number = search.ticket_number;
            }

            if (search.date) {
                filters.date = search.date;
            }

            // Add is_active filter to get active tickets
            if (search.is_active) {
                filters.is_active = search.is_active;
            }

            console.log('Fetching tickets with filters:', filters);

            const response = await getWeighbridgeTicketList(filters);

            if (response.success) {
                let tickets = response.data.data || [];
                let meta = response.data || {};

                // Calculate status counts from tickets
                const statusCounts = {
                    total: meta.total || 0,
                    IN: 0,
                    OUT: 0,
                    WGH: 0
                };

                // Count tickets by status
                tickets.forEach(ticket => {
                    if (ticket.status && statusCounts[ticket.status] !== undefined) {
                        statusCounts[ticket.status]++;
                    }
                });

                this.setState({
                    tickets: tickets,
                    total: meta.total || 0,
                    statusData: [
                        { key: 'all', label: 'All', count: statusCounts.total || 0 },
                        { key: 'IN', label: 'In Progress', count: statusCounts.IN || 0 },
                        { key: 'OUT', label: 'Completed', count: statusCounts.OUT || 0 },
                    ],
                    is_loading: false,
                });

                console.log('Tickets loaded:', tickets.length);
            } else {
                console.error('Failed to fetch weighbridge tickets:', response.message);
                this.setState({
                    tickets: [],
                    is_loading: false
                });
            }
        } catch (error) {
            console.error('Error fetching weighbridge tickets:', error);
            this.setState({
                tickets: [],
                is_loading: false
            });
        }
    }

    handleTabChange = (key) => {
        this.setState({ status: key, page: 1 }, () => {
            this.fetchWeighbridgeTickets();
        });
    };

    handlePageChange = (page) => {
        this.setState({ page: page }, () => {
            this.fetchWeighbridgeTickets();
        });
    };

    handleRowsPerPageChange = (newLimit, page) => {
        this.setState({ limit: newLimit, page: 1 }, () => {
            this.fetchWeighbridgeTickets();
        });
    };

    handleRowClick = (row, e) => {
        console.log('Row clicked:', row);
        if (this.props.history && row.id) {
            this.props.history.push(`/weighbridge-ticket/view/${row.id}`);
        }
    };

    handleEdit = (row, e) => {
        e?.stopPropagation();
        if (this.props.history && row.id) {
            this.props.history.push(`/weighbridge-ticket/view/${row.id}`);
        }
    };

    // Export Functions
    handleExport = () => {
        this.setState({ showExportModal: true });
    };

    handleExportFilterChange = (field, value) => {
        this.setState(prevState => ({
            exportFilters: {
                ...prevState.exportFilters,
                [field]: value
            }
        }));
    };

    exportToExcel = async () => {
        const { exportFilters } = this.state;

        console.log('Export filters being sent:', exportFilters); // Add this log

        // Validate dates
        if (new Date(exportFilters.start_date) > new Date(exportFilters.end_date)) {
            toast.error('Start date cannot be after end date');
            return;
        }

        this.setState({ is_exporting: true });

        try {
            const response = await exportWeighbridgeTickets(exportFilters);

            console.log('Export response:', response);

            if (response.data) {
                // If the API returns a blob (Excel file)
                if (response.data instanceof Blob) {
                    // Create download link
                    const url = window.URL.createObjectURL(response.data);
                    const link = document.createElement('a');
                    link.href = url;

                    // Generate filename
                    const filename = `DAILY_COLLECTION_REPORT_${exportFilters.start_date}_TO_${exportFilters.end_date}.xlsx`;
                    link.setAttribute('download', filename);

                    document.body.appendChild(link);
                    link.click();
                    link.remove();

                    // Clean up URL
                    window.URL.revokeObjectURL(url);
                } else {
                    // If API returns JSON data (for manual Excel generation)
                    this.generateExcelFromData(response.data);
                }

                // Close modal on success
                this.setState({ showExportModal: false });
            } else {
                console.error('Export failed:', response.message);
                toast.error('Failed to export report: ' + (response.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error exporting report:', error);
            toast.error('Error exporting report: ' + error.message);
        } finally {
            this.setState({ is_exporting: false });
        }
    };

    generateExcelFromData = (data) => {
        // This function would generate Excel from JSON data
        // You can use a library like SheetJS (xlsx) or similar
        console.log('Data for Excel generation:', data);

        // For now, just show toast.error with data summary
        if (data.summary) {
            toast.success(`Report generated successfully!\n\nTotal Tickets: ${data.summary.total_tickets}\nTotal Net Weight: ${data.summary.total_net_weight_mt} MT`);
        }
    };

    // Helper function to format status badge
    getStatusBadge = (status) => {
        const statusMap = {
            'IN': { label: 'In Progress', color: '#3b82f6', bgColor: '#dbeafe' },
            'OUT': { label: 'Completed', color: '#10b981', bgColor: '#d1fae5' },
        };

        const statusInfo = statusMap[status] || { label: status, color: '#6b7280', bgColor: '#f3f4f6' };

        return (
            <span style={{
                backgroundColor: statusInfo.bgColor,
                color: statusInfo.color,
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
            }}>
                <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: statusInfo.color,
                }} />
                {statusInfo.label}
            </span>
        );
    };

    // Format date for display
    formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    formatTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    formatWeight = (weight) => {
        return parseFloat(weight || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    getColumns() {
        return [
            {
                name: '#',
                selector: (row, index) => index + 1,
                width: '60px',
                center: true,
            },
            // {
            //     name: '',
            //     center: true,
            //     width: '70px',
            //     cell: (row) => (
            //         <div className="d-flex align-items-center justify-content-center gap-2">
            //             <Link
            //                 className="button1 button1-primary"
            //                 to={`/weighbridge-ticket/view/${row.id}`}
            //                 onClick={(e) => this.handleEdit(row, e)}
            //             >
            //                 <Eye size={14} />
            //             </Link>
            //         </div>
            //     ),
            // },
            {
                name: 'Ticket Number',
                selector: row => row.ticket_number,
                sortable: true,
                width: '150px',
                cell: row => (
                    <Link
                        to={`/weighbridge-ticket/view/${row.id}`}
                        onClick={(e) => this.handleEdit(row, e)}
                    >
                        <div style={{ fontWeight: '600', color: '#1e40af' }}>
                            {row.ticket_number}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            {this.formatDate(row.created_at)}
                        </div>
                    </Link>
                ),
            },
            {
                name: 'Vehicle',
                selector: row => row.vehicle?.vehicle_number || '-',
                sortable: true,
                width: '120px',
                cell: row => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Truck size={14} color="#6b7280" />
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>
                            {row.vehicle?.vehicle_number || '-'}
                        </div>
                    </div>
                ),
            },
            {
                name: 'Driver',
                selector: row => row.driver?.name || '-',
                sortable: true,
                width: '150px',
                cell: row => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <User size={14} color="#6b7280" />
                        <span style={{ fontSize: '13px' }}>
                            {row.driver?.name || '-'}
                        </span>
                    </div>
                ),
            },
            {
                name: 'Delivery Site',
                selector: row => row.delivery_site?.auction_name || '-',
                sortable: true,
                width: '180px',
                cell: row => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MapPin size={14} color="#6b7280" />
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: '500' }}>
                                {row.delivery_site?.auction_name || '-'}
                            </div>
                            {row.delivery_site?.progress !== undefined && (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                    Progress: {parseFloat(row.delivery_site?.progress || 0).toFixed(1)}%
                                </div>
                            )}
                        </div>
                    </div>
                ),
            },
            {
                name: 'Weight Details',
                width: '180px',
                cell: row => {
                    const tareWeight = parseFloat(row.tare_weight) || 0;
                    const grossWeight = parseFloat(row.gross_weight) || 0;
                    const netWeight = parseFloat(row.net_weight) || 0;

                    return (
                        <div className="weight-details" style={{ fontSize: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span style={{ color: '#6b7280' }}>Tare:</span>
                                <span style={{ fontWeight: '600' }}>{this.formatWeight(tareWeight)} kg</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span style={{ color: '#6b7280' }}>Gross:</span>
                                <span style={{ fontWeight: '600', color: grossWeight > 0 ? '#3b82f6' : '#6b7280' }}>
                                    {grossWeight > 0 ? this.formatWeight(grossWeight) + ' kg' : '-'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#6b7280' }}>Net:</span>
                                <span style={{ fontWeight: '600', color: netWeight > 0 ? '#10b981' : '#6b7280' }}>
                                    {netWeight > 0 ? this.formatWeight(netWeight) + ' kg' : '-'}
                                </span>
                            </div>
                        </div>
                    );
                },
            },
            {
                name: 'Yard',
                cell: row => (
                    <div style={{ fontSize: '12px' }}>
                        {row.yard ? (
                            <div>
                                <div style={{ fontWeight: '500', color: '#1e40af' }}>
                                    {row.yard.name}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                    Loaded: {this.formatWeight(row.yard.loaded_weight)} kg
                                </div>
                            </div>
                        ) : (
                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No yard</span>
                        )}
                    </div>
                ),
            },
            {
                name: 'Timing',
                cell: row => (
                    <div style={{ fontSize: '12px' }}>
                        <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Entry: <span style={{ fontWeight: '500' }}>
                                {row.entry_time ? this.formatTime(row.entry_time) : '-'}
                            </span>
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Exit:
                                <span style={{ fontWeight: '500', color: row.exit_time ? '#10b981' : '#6b7280' }}>
                                    {row.exit_time ? this.formatTime(row.exit_time) : '-'}
                                </span></div>
                        </div>
                        {row.entry_time && row.exit_time && (
                            <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>
                                Duration: {row.entry_time && row.exit_time ?
                                    Math.round((new Date(row.exit_time) - new Date(row.entry_time)) / (1000 * 60)) + ' min' :
                                    '-'}
                            </div>
                        )}
                    </div>
                ),
            },
            {
                name: 'Status',
                selector: row => row.status,
                sortable: true,
                width: '120px',
                cell: row => this.getStatusBadge(row.status),
            },
            // {
            //     name: 'Actions',
            //     width: '80px',
            //     center: true,
            //     cell: (row) => (
            //         <div className="d-flex align-items-center justify-content-center gap-2">
            //             <Link
            //                 className="button1 button1-primary"
            //                 to={`/weighbridge-ticket/view/${row.id}`}
            //                 onClick={(e) => this.handleEdit(row, e)}
            //             >
            //                 <Pen size={14} />
            //             </Link>
            //         </div>
            //     ),
            // },
        ];
    }

    handleSearchChange = (field, value) => {
        this.setState(prevState => ({
            search: {
                ...prevState.search,
                [field]: value
            }
        }));
    };

    handleSearchSubmit = () => {
        this.setState({ page: 1 }, () => {
            this.fetchWeighbridgeTickets();
        });
    };

    handleClearFilters = () => {
        const today = new Date().toISOString().split('T')[0];

        this.setState({
            search: {
                vehicle_id: '',
                delivery_site_id: '',
                status: '',
                driver_id: '',
                ticket_number: '',
                date: '',
            },
            status: 'all',
            page: 1
        }, () => {
            this.fetchWeighbridgeTickets();
        });
    };

    // Export Modal component
    ExportModal = () => {
        const { showExportModal, exportFilters, is_exporting } = this.state;

        if (!showExportModal) return null;

        const today = new Date().toISOString().split('T')[0];

        return (
            <div className="modal-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}>
                <div className="modal-content" style={{
                    backgroundColor: 'white',
                    padding: '30px',
                    borderRadius: '12px',
                    maxWidth: '500px',
                    width: '90%',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                }}>
                    <div className="modal-header" style={{
                        marginBottom: '20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <h3 style={{ margin: 0, color: '#1e40af' }}>
                            <FileSpreadsheet size={20} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
                            Export Daily Collection Report
                        </h3>
                        <button
                            onClick={() => this.setState({ showExportModal: false })}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '24px',
                                cursor: 'pointer',
                                color: '#6b7280',
                            }}
                        >
                            &times;
                        </button>
                    </div>

                    <div className="modal-body">
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                                <Calendar size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                Date Range
                            </label>
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <input
                                        type="date"
                                        value={exportFilters.start_date}
                                        onChange={(e) => this.handleExportFilterChange('start_date', e.target.value)}
                                        max={exportFilters.end_date}
                                        min="2026-01-01"  // Add minimum date based on your data
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                        }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Start Date</div>
                                </div>
                                <span style={{ color: '#6b7280', fontWeight: '500' }}>to</span>
                                <div style={{ flex: 1 }}>
                                    <input
                                        type="date"
                                        value={exportFilters.end_date}
                                        onChange={(e) => this.handleExportFilterChange('end_date', e.target.value)}
                                        min={exportFilters.start_date}
                                        max={today}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                        }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>End Date</div>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                                Ticket Status
                            </label>
                            <select
                                value={exportFilters.status}
                                onChange={(e) => this.handleExportFilterChange('status', e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: 'white',
                                }}
                            >
                                <option value="OUT">Completed Tickets Only</option>
                                <option value="IN">In Progress Only</option>
                                <option value="all">All Statuses</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #dbeafe' }}>
                            <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: '500', marginBottom: '5px' }}>
                                <InfoIcon size={14} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                                Report Format
                            </div>
                            <div style={{ fontSize: '12px', color: '#4b5563' }}>
                                The exported report will be in Excel format (.xlsx) matching the Daily Collection Report template.
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
                            <button
                                onClick={() => this.setState({ showExportModal: false })}
                                disabled={is_exporting}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#f3f4f6',
                                    color: '#374151',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={this.exportToExcel}
                                disabled={is_exporting}
                                style={{
                                    padding: '10px 25px',
                                    backgroundColor: is_exporting ? '#93c5fd' : '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                {is_exporting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Exporting...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} />
                                        Export Report
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { tickets, is_loading, statusData, status, page, limit, total, hideSidebar } = this.state;

        const customTableStyles = {
            ...tableStyles,
            rows: {
                ...tableStyles.rows,
                style: {
                    ...tableStyles.rows?.style,
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: '#f8f9fa',
                    },
                },
            },
        };

        return (
            <React.Fragment>
                <HeaderBanner
                    title={i18n.t('weighbridge_tickets')}
                    subtitle={i18n.t('manage_weighbridge_tickets')}
                />
                <section className="mt-5">
                    <div className="container">
                        <div className="row">
                            <div className="col-xl-3 col-lg-12 col-md-12">
                                {!hideSidebar && <SideBar />}
                            </div>
                            <div
                                className={
                                    hideSidebar
                                        ? 'col-xl-12 col-lg-12 col-md-12'
                                        : 'col-xl-9 col-lg-12 col-md-12'
                                }
                            >
                                <div className="card">
                                    <div className="card-header">
                                        <div className="expand-dashboard">
                                            <span
                                                style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                                                onClick={() => {
                                                    this.setState({
                                                        hideSidebar: !hideSidebar,
                                                    });
                                                }}
                                            >
                                                {hideSidebar && <i className="icon-size-actual"></i>}
                                                {!hideSidebar && <i className="icon-size-fullscreen"></i>}
                                            </span>
                                        </div>
                                        {/* Add Export Button */}
                                        <button
                                            onClick={this.handleExport}
                                            className="button1 button1-success"
                                            style={{ float: 'right', marginLeft: '10px' }}
                                        >
                                            <Download size={16} style={{ marginRight: '8px' }} />
                                            Export Report
                                        </button>
                                    </div>
                                    <div className="card-body">
                                        {/* Search Filters */}
                                        <FilterSection
                                            loading={this.state.isLoading}
                                            onSearch={this.handleSearchSubmit}
                                            onClear={this.handleClearFilters}
                                        >
                                            <div className="select-wrapper floating-label-wrapper">
                                                <input
                                                    type="text"
                                                    className="filter-base floating-input"
                                                    value={this.state.search.ticket_number}
                                                    onChange={(e) => this.handleSearchChange('ticket_number', e.target.value)}
                                                />
                                                <label className="floating-label">Ticket Number</label>
                                            </div>

                                            <div className="select-wrapper floating-label-wrapper">
                                                <input
                                                    type="text"
                                                    className="filter-base floating-input"
                                                    value={this.state.search.vehicle_id}
                                                    onChange={(e) => this.handleSearchChange('vehicle_id', e.target.value)}
                                                />
                                                <label className="floating-label">Vehicle</label>
                                            </div>

                                            <div className="select-wrapper floating-label-wrapper">
                                                <input
                                                    type="date"
                                                    className="filter-base floating-input"
                                                    value={this.state.search.date}
                                                    onChange={(e) => this.handleSearchChange('date', e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                />
                                                <label className="floating-label">Date</label>
                                            </div>
                                        </FilterSection>

                                        <StatusTabs
                                            tabs={statusData}
                                            onTabChange={this.handleTabChange}
                                            selected={status}
                                        />

                                        <div className="Data-Listing">
                                            {is_loading ? (
                                                <TableSkeleton columns={8} rows={10} />
                                            ) : tickets.length ? (
                                                <DataTable
                                                    key={`${page}-${limit}`}
                                                    title=""
                                                    columns={this.getColumns()}
                                                    data={tickets}
                                                    customStyles={customTableStyles}
                                                    highlightOnHover
                                                    pagination
                                                    paginationServer
                                                    paginationTotalRows={total}
                                                    paginationPerPage={limit}
                                                    paginationDefaultPage={page}
                                                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                                                    paginationComponentOptions={{
                                                        rowsPerPageText: i18n.t('rows_per_page'),
                                                        rangeSeparatorText: i18n.t('out'),
                                                    }}
                                                    onChangeRowsPerPage={this.handleRowsPerPageChange}
                                                    onChangePage={this.handlePageChange}
                                                    onRowClicked={this.handleRowClick}
                                                />
                                            ) : (
                                                <NoData
                                                    message="No weighbridge tickets available"
                                                    actionText="Refresh"
                                                    onAction={this.fetchWeighbridgeTickets.bind(this)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Export Modal */}
                {this.ExportModal()}
            </React.Fragment>
        );
    }
}

export default WeighbridgeTicketList;